import { useState } from 'react';
import { Alert, api, apiBaseUrl, Badge, Button, Card, DataTable, fmtDate, Input, ListPage, Modal, Select, Spinner, tokenStore, useFetch, useForm, usePagedList } from '@greenrinse/ui';
import type { Partner, Site } from './types';

export const VEHICLE_TYPES = ['Unknown', 'Sedan', 'Suv', 'Van', 'Pickup', 'Truck', 'Bus', 'Motorcycle'] as const;
export const VEHICLE_STATUSES = ['Active', 'InMaintenance', 'Sold', 'Inactive'] as const;

export interface Vehicle {
  id: string; partnerId: string; partnerName: string; siteId: string | null; siteName: string | null; plate: string; plateNormalized: string;
  plateCountry: string | null; plateRegion: string | null; make: string | null; model: string | null; type: string; color: string | null; year: number | null;
  vin: string | null; externalRef: string | null; status: string; notes: string | null; createdAtUtc: string; updatedAtUtc: string | null;
}

interface HistoryRow { validFromUtc: string; validToUtc: string; plate: string; type: string; color: string | null; status: string; siteId: string | null; isDeleted: boolean }

interface ImportReport {
  batchId: string; status: string; fileName: string; totalRows: number; validRows: number; errorRows: number; willInsert: number; willUpdate: number; willDeactivate: number;
  errors: { row: number; plate: string | null; errors: string[] }[]; unknownColumns: string[]; inserted: number; updated: number; deactivated: number;
}

type Tone = 'default' | 'success' | 'warning' | 'error' | 'accent';
function statusTone(s: string): Tone { return s === 'Active' ? 'success' : s === 'InMaintenance' ? 'warning' : 'default'; }

export function VehicleForm({ vehicle, partnerId, onSaved, onCancel }: { vehicle: Vehicle | null; partnerId?: string; onSaved: () => void; onCancel: () => void }) {
  const partners = useFetch<{ items: Partner[] }>(vehicle || partnerId ? null : '/api/partners?pageSize=200');
  const form = useForm({
    partnerId: vehicle?.partnerId ?? partnerId ?? '', siteId: vehicle?.siteId ?? '', plate: vehicle?.plate ?? '', plateCountry: vehicle?.plateCountry ?? '', plateRegion: vehicle?.plateRegion ?? '',
    make: vehicle?.make ?? '', model: vehicle?.model ?? '', type: vehicle?.type ?? 'Sedan', color: vehicle?.color ?? '', year: vehicle?.year?.toString() ?? '', vin: vehicle?.vin ?? '',
    externalRef: vehicle?.externalRef ?? '', status: vehicle?.status ?? 'Active', notes: vehicle?.notes ?? '',
  });
  const effectivePartner = form.values.partnerId || partners.data?.items[0]?.id || '';
  const sites = useFetch<{ items: Site[] }>(effectivePartner ? `/api/sites?partnerId=${effectivePartner}&pageSize=200` : null, [effectivePartner]);

  const save = () => form.submit(async (v) => {
    const body = { ...v, partnerId: effectivePartner, siteId: v.siteId || null, plateCountry: v.plateCountry || null, plateRegion: v.plateRegion || null, make: v.make || null, model: v.model || null,
      color: v.color || null, year: v.year ? Number(v.year) : null, vin: v.vin || null, externalRef: v.externalRef || null, notes: v.notes || null };
    if (vehicle) await api(`/api/fleet/vehicles/${vehicle.id}`, { method: 'PUT', body }); else await api('/api/fleet/vehicles', { method: 'POST', body });
    onSaved();
  });

  return (
    <form onSubmit={(e) => { e.preventDefault(); void save(); }} className="gr-form-grid">
      {form.message && <div className="gr-span-2" style={{ marginBottom: 12 }}><Alert tone="error">{form.message}</Alert></div>}
      {!vehicle && !partnerId && <Select label="Partner" value={effectivePartner} onChange={(e) => form.set('partnerId', e.target.value)} options={(partners.data?.items ?? []).map((p) => ({ value: p.id, label: p.name }))} />}
      <Select label="Site (optional)" {...form.bind('siteId')} options={[{ value: '', label: '— none —' }, ...(sites.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }))]} />
      <Input label="Plate" plate {...form.bind('plate')} />
      <Select label="Type" {...form.bind('type')} options={VEHICLE_TYPES.map((t) => ({ value: t, label: t }))} />
      <Input label="Plate country (ISO)" maxLength={2} {...form.bind('plateCountry')} />
      <Input label="Plate region / emirate" {...form.bind('plateRegion')} />
      <Input label="Make" {...form.bind('make')} />
      <Input label="Model" {...form.bind('model')} />
      <Input label="Colour" {...form.bind('color')} />
      <Input label="Year" type="number" {...form.bind('year')} />
      <Input label="VIN" {...form.bind('vin')} />
      <Input label="Partner reference / asset no" {...form.bind('externalRef')} />
      <Select label="Status" {...form.bind('status')} options={VEHICLE_STATUSES.map((s) => ({ value: s, label: s }))} />
      <div className="gr-span-2 gr-field"><label>Notes</label><textarea className="gr-textarea" rows={2} value={form.values.notes} onChange={(e) => form.set('notes', e.target.value)} /></div>
      <div className="gr-span-2 gr-row" style={{ justifyContent: 'flex-end' }}>
        <Button type="button" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button type="submit" loading={form.busy}>Save</Button>
      </div>
    </form>
  );
}

function VehicleHistory({ vehicle }: { vehicle: Vehicle }) {
  const h = useFetch<HistoryRow[]>(`/api/fleet/vehicles/${vehicle.id}/history`);
  if (h.loading) return <Spinner />;
  if (!h.data || h.data.length === 0) return <p className="gr-muted">No history yet (history is recorded on SQL Server only).</p>;
  return (
    <DataTable<HistoryRow> rows={h.data} rowKey={(r) => r.validFromUtc}
      columns={[
        { key: 'from', header: 'From', render: (r) => fmtDate(r.validFromUtc) },
        { key: 'to', header: 'To', render: (r) => r.validToUtc.startsWith('9999') ? <Badge tone="success">current</Badge> : fmtDate(r.validToUtc) },
        { key: 'plate', header: 'Plate', render: (r) => r.plate },
        { key: 'type', header: 'Type', render: (r) => r.type },
        { key: 'status', header: 'Status', render: (r) => r.isDeleted ? <Badge tone="error">Deleted</Badge> : <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
      ]} />
  );
}

function ImportWizard({ partnerId, onDone }: { partnerId: string; onDone: () => void }) {
  const sites = useFetch<{ items: Site[] }>(`/api/sites?partnerId=${partnerId}&pageSize=200`);
  const [file, setFile] = useState<File | null>(null);
  const [siteId, setSiteId] = useState('');
  const [deactivateMissing, setDeactivateMissing] = useState(false);
  const [report, setReport] = useState<ImportReport | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ inserted: number; updated: number; deactivated: number } | null>(null);

  const validate = async () => {
    if (!file) return;
    setBusy(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file); fd.append('partnerId', partnerId);
      if (siteId) fd.append('siteId', siteId);
      fd.append('deactivateMissing', String(deactivateMissing));
      setReport(await api<ImportReport>('/api/fleet/import/validate', { method: 'POST', form: fd }));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const commit = async () => {
    if (!report) return;
    setBusy(true); setError(null);
    try { setResult(await api(`/api/fleet/import/${report.batchId}/commit`, { method: 'POST' })); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  const downloadTemplate = async () => {
    const res = await fetch(`${apiBaseUrl}/api/fleet/import/template`, { headers: { Authorization: `Bearer ${tokenStore.get()?.accessToken ?? ''}` } });
    const blob = await res.blob();
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = 'greenrinse-fleet-template.xlsx'; a.click(); URL.revokeObjectURL(a.href);
  };

  if (result) {
    return (
      <div className="gr-stack">
        <Alert tone="success">Import complete: {result.inserted} added · {result.updated} updated · {result.deactivated} deactivated.</Alert>
        <Button onClick={onDone}>Close</Button>
      </div>
    );
  }

  return (
    <div className="gr-stack">
      {error && <Alert tone="error">{error}</Alert>}
      {!report ? (
        <>
          <p className="gr-muted">Upload a CSV or Excel file. Only the <strong>Plate</strong> column is required; other columns are matched by name. <button type="button" className="gr-btn gr-btn--ghost" style={{ minHeight: 28, padding: '0 8px' }} onClick={() => void downloadTemplate()}>Download template</button></p>
          <input type="file" accept=".csv,.xlsx,.txt" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <Select label="Default site for imported vehicles (optional)" value={siteId} onChange={(e) => setSiteId(e.target.value)}
            options={[{ value: '', label: '— none —' }, ...(sites.data?.items ?? []).map((s) => ({ value: s.id, label: s.name }))]} />
          <label className="gr-row"><input type="checkbox" checked={deactivateMissing} onChange={(e) => setDeactivateMissing(e.target.checked)} /> Deactivate vehicles not in this file (full fleet replacement)</label>
          <div className="gr-row" style={{ justifyContent: 'flex-end' }}><Button onClick={() => void validate()} disabled={!file} loading={busy}>Validate</Button></div>
        </>
      ) : (
        <>
          <Card tonal small>
            <div className="gr-kv">
              <dt>File</dt><dd>{report.fileName}</dd>
              <dt>Rows</dt><dd>{report.totalRows} total · <span style={{ color: 'var(--gr-success)' }}>{report.validRows} valid</span> · <span style={{ color: 'var(--gr-error)' }}>{report.errorRows} with errors</span></dd>
              <dt>Will apply</dt><dd>+{report.willInsert} new · ~{report.willUpdate} updated · −{report.willDeactivate} deactivated</dd>
              {report.unknownColumns.length > 0 && <><dt>Ignored columns</dt><dd className="gr-muted">{report.unknownColumns.join(', ')}</dd></>}
            </div>
          </Card>
          {report.errors.length > 0 && (
            <div className="gr-card gr-card--sm" style={{ padding: 0 }}>
              <DataTable rows={report.errors} rowKey={(r) => String(r.row)}
                columns={[
                  { key: 'row', header: 'Row', render: (r) => r.row, width: 60 },
                  { key: 'plate', header: 'Plate', render: (r) => r.plate ?? '—' },
                  { key: 'err', header: 'Problem', render: (r) => r.errors.join(' ') },
                ]} />
            </div>
          )}
          <Alert tone="info">Rows with errors are skipped. Fix the file and validate again, or commit the valid rows now.</Alert>
          <div className="gr-row" style={{ justifyContent: 'flex-end' }}>
            <Button variant="outline" onClick={() => setReport(null)}>Back</Button>
            <Button onClick={() => void commit()} disabled={report.validRows === 0} loading={busy}>Commit {report.validRows} rows</Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function FleetPage({ partnerId }: { partnerId?: string }) {
  const [filterPartner, setFilterPartner] = useState(partnerId ?? '');
  const [status, setStatus] = useState('');
  const partners = useFetch<{ items: Partner[] }>(partnerId ? null : '/api/partners?pageSize=200');
  const effectivePartner = partnerId ?? filterPartner;
  const list = usePagedList<Vehicle>('/api/fleet/vehicles', { partnerId: effectivePartner || undefined, status: status || undefined });
  const [editing, setEditing] = useState<Vehicle | 'new' | null>(null);
  const [importing, setImporting] = useState(false);
  const [history, setHistory] = useState<Vehicle | null>(null);

  return (
    <>
      <ListPage<Vehicle>
        title="Fleet" state={list} rowKey={(v) => v.id} onRowClick={(v) => setEditing(v)}
        actions={<>
          <Button variant="tonal" disabled={!effectivePartner} onClick={() => setImporting(true)}>Import CSV / Excel</Button>
          <Button onClick={() => setEditing('new')}>Add vehicle</Button>
        </>}
        filters={<>
          {!partnerId && (
            <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={filterPartner} onChange={(e) => setFilterPartner(e.target.value)}>
              <option value="">All partners</option>
              {(partners.data?.items ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          )}
          <select className="gr-select" style={{ width: 'auto', minHeight: 40 }} value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All statuses</option>
            {VEHICLE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </>}
        columns={[
          { key: 'plate', header: 'Plate', render: (v) => <strong style={{ fontFamily: 'var(--gr-font-heading)', letterSpacing: '0.06em' }}>{v.plate}</strong> },
          ...(partnerId ? [] : [{ key: 'partner', header: 'Partner', render: (v: Vehicle) => v.partnerName }]),
          { key: 'vehicle', header: 'Vehicle', render: (v) => <>{[v.make, v.model].filter(Boolean).join(' ') || '—'}<div className="gr-muted" style={{ fontSize: 13 }}>{v.type}{v.color ? ` · ${v.color}` : ''}{v.year ? ` · ${v.year}` : ''}</div></> },
          { key: 'site', header: 'Site', render: (v) => v.siteName ?? '—' },
          { key: 'ref', header: 'Ref', render: (v) => v.externalRef ?? '—' },
          { key: 'status', header: 'Status', render: (v) => <Badge tone={statusTone(v.status)}>{v.status}</Badge> },
          { key: 'hist', header: '', render: (v) => <button className="gr-btn gr-btn--ghost" style={{ minHeight: 30, padding: '0 10px' }} onClick={(e) => { e.stopPropagation(); setHistory(v); }}>History</button> },
        ]}
      />
      <Modal open={editing !== null} title={editing === 'new' ? 'New vehicle' : `Edit ${editing?.plate ?? ''}`} onClose={() => setEditing(null)}>
        {editing !== null && <VehicleForm vehicle={editing === 'new' ? null : editing} partnerId={partnerId} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); list.reload(); }} />}
      </Modal>
      <Modal open={importing} wide title="Import fleet list" onClose={() => setImporting(false)}>
        {importing && effectivePartner && <ImportWizard partnerId={effectivePartner} onDone={() => { setImporting(false); list.reload(); }} />}
      </Modal>
      <Modal open={history !== null} wide title={`History · ${history?.plate ?? ''}`} onClose={() => setHistory(null)}>
        {history && <VehicleHistory vehicle={history} />}
      </Modal>
    </>
  );
}
